import React from 'react';
import { Watch, CheckCircle, Heart, TrendingUp, Users, Award, ArrowRight, Shield, Bell, Activity, Smartphone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { useLanguage } from '../../contexts/LanguageContext';

const ElderBand = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Yamai ElderBand',
      subtitle: 'Smart Safety Bracelet for Elderly Protection',
      badge: 'In R&D',
      overview: {
        title: 'Overview',
        text1: 'Yamai ElderBand is an advanced smart bracelet specifically designed to protect elderly people living alone. It combines advanced biosensors with AI capable of detecting any dangerous changes in health status or movement and sending immediate alerts to family members or emergency services.',
        text2: 'Unlike generic fitness trackers, ElderBand is purpose-built for elderly safety with medical-grade sensors, advanced fall detection algorithms, and emergency response features.'
      },
      problem: {
        title: 'The Problem',
        cards: [
          {
            title: 'Delayed Emergency Response',
            text: 'When elderly people fall or experience a cardiac event while alone, critical minutes are lost before help arrives, significantly reducing survival chances.'
          },
          {
            title: 'Silent Health Deterioration',
            text: 'Gradual changes in vital signs often go unnoticed until a crisis occurs, missing opportunities for early intervention.'
          },
          {
            title: 'Social Isolation Risks',
            text: 'Living alone increases vulnerability to undetected medical emergencies, creating anxiety for both elderly individuals and their families.'
          },
          {
            title: 'False Alarm Problem',
            text: 'Traditional alert systems generate too many false alarms, leading to alert fatigue and delayed response to real emergencies.'
          }
        ]
      },
      solution: {
        title: 'Our Solution',
        subtitle: 'Key Features'
      },
      features: [
        {
          icon: Heart,
          title: 'Continuous Vital Signs Monitoring',
          items: [
            'Heart rate and rhythm analysis',
            'Blood oxygen saturation (SpO2)',
            'Body temperature tracking',
            'Movement and activity patterns'
          ]
        },
        {
          icon: Shield,
          title: 'Advanced AI Fall Detection',
          items: [
            'Machine learning algorithms distinguish real falls from normal movements',
            'Impact force and orientation analysis',
            'Post-fall immobility detection',
            'Automatic emergency alert if no response within 30 seconds'
          ]
        },
        {
          icon: Bell,
          title: 'Emergency SOS Button',
          items: [
            'One-press emergency call to family',
            'Direct connection to emergency services (112)',
            'GPS location sharing',
            'Two-way voice communication capability'
          ]
        },
        {
          icon: Activity,
          title: 'Golden Minutes Monitoring',
          items: [
            'Instant alert on sudden pulse stoppage',
            'Abnormal heart rhythm detection',
            'Prolonged immobility warnings',
            'Predictive risk assessment based on patterns'
          ]
        },
        {
          icon: Smartphone,
          title: 'Family Mobile App',
          items: [
            'Real-time health status dashboard',
            'Instant push notifications for alerts',
            'Daily activity and sleep reports',
            'Multiple family member access with permission levels'
          ]
        }
      ],
      technology: {
        title: 'Technology',
        stack: [
          'Medical-grade PPG sensors for heart rate and SpO2',
          'Advanced 3-axis accelerometer and gyroscope',
          'Skin temperature sensor',
          'TensorFlow Lite AI models for on-device fall detection',
          'Low-power Bluetooth and LTE-M connectivity',
          'IP68 water resistance for shower safety',
          '7-day battery life with wireless charging'
        ]
      },
      roadmap: {
        title: 'Development Roadmap',
        phases: [
          { period: 'Q1 2026', title: 'Prototype Development', status: 'Planned' },
          { period: 'Q2-Q3 2026', title: 'Clinical Testing with Elderly Volunteers', status: 'Planned' },
          { period: 'Q4 2026', title: 'AI Model Training & Validation', status: 'Planned' },
          { period: 'Q1-Q2 2027', title: 'MDR Certification Process', status: 'Planned' }
        ]
      },
      impact: {
        title: 'Expected Impact',
        metrics: [
          { metric: '85%', label: 'Fall Detection Accuracy', icon: Award },
          { metric: '<30 sec', label: 'Emergency Response Time', icon: TrendingUp },
          { metric: '24/7', label: 'Continuous Monitoring', icon: Activity },
          { metric: '100%', label: 'Family Peace of Mind', icon: Users }
        ]
      },
      cta: {
        title: 'Get Involved',
        text: 'Interested in partnering, investing, or collaborating on this project?',
        button: 'Contact Us',
        back: 'Back to Projects'
      }
    },
    nl: {
      title: 'Yamai ElderBand',
      subtitle: 'Slimme Veiligheidsarmband voor Oudere Bescherming',
      badge: 'In O&O',
      overview: {
        title: 'Overzicht',
        text1: 'Yamai ElderBand is een geavanceerde slimme armband speciaal ontworpen om ouderen die alleen wonen te beschermen. Het combineert geavanceerde biosensoren met AI die gevaarlijke veranderingen in gezondheidstoestand of beweging kan detecteren en onmiddellijke waarschuwingen naar familieleden of hulpdiensten kan sturen.',
        text2: 'In tegenstelling tot generieke fitness trackers is ElderBand speciaal gebouwd voor oudere veiligheid met medische sensoren, geavanceerde valdetectie-algoritmen en noodresponsfuncties.'
      },
      problem: {
        title: 'Het Probleem',
        cards: [
          {
            title: 'Vertraagde Noodrespons',
            text: 'Wanneer ouderen vallen of een hartprobleem krijgen terwijl ze alleen zijn, gaan kritieke minuten verloren voordat hulp arriveert, wat overlevingskansen aanzienlijk vermindert.'
          },
          {
            title: 'Stille Gezondheidsverslechtering',
            text: 'Geleidelijke veranderingen in vitale functies blijven vaak onopgemerkt tot een crisis optreedt, waardoor kansen voor vroege interventie worden gemist.'
          },
          {
            title: 'Sociaal Isolatierisico',
            text: 'Alleen wonen vergroot de kwetsbaarheid voor onopgemerkte medische noodgevallen, wat angst creëert voor zowel ouderen als hun families.'
          },
          {
            title: 'Vals Alarm Probleem',
            text: 'Traditionele alarmsystemen genereren te veel valse alarmen, wat leidt tot alarmmoeheid en vertraagde reactie op echte noodgevallen.'
          }
        ]
      },
      solution: {
        title: 'Onze Oplossing',
        subtitle: 'Belangrijkste Functies'
      },
      features: [
        {
          icon: Heart,
          title: 'Continue Vitale Functies Monitoring',
          items: [
            'Hartslag en ritme-analyse',
            'Bloedzuurstofverzadiging (SpO2)',
            'Lichaamstemperatuur tracking',
            'Bewegings- en activiteitspatronen'
          ]
        },
        {
          icon: Shield,
          title: 'Geavanceerde AI Valdetectie',
          items: [
            'Machine learning algoritmen onderscheiden echte vallen van normale bewegingen',
            'Impact kracht en oriëntatie-analyse',
            'Post-val immobiliteit detectie',
            'Automatisch noodmelding als geen reactie binnen 30 seconden'
          ]
        },
        {
          icon: Bell,
          title: 'Nood-SOS Knop',
          items: [
            'Eén-druk noodoproep naar familie',
            'Directe verbinding met hulpdiensten (112)',
            'GPS locatie delen',
            'Twee-weg spraakcommunicatie mogelijkheid'
          ]
        },
        {
          icon: Activity,
          title: 'Gouden Minuten Monitoring',
          items: [
            'Onmiddellijke waarschuwing bij plotselinge polsstop',
            'Abnormaal hartritme detectie',
            'Langdurige immobiliteit waarschuwingen',
            'Voorspellende risicobeoordeling op basis van patronen'
          ]
        },
        {
          icon: Smartphone,
          title: 'Familie Mobiele App',
          items: [
            'Real-time gezondheidsstatus dashboard',
            'Onmiddellijke push-notificaties voor waarschuwingen',
            'Dagelijkse activiteit en slaap rapporten',
            'Meerdere familieleden toegang met toestemmingsniveaus'
          ]
        }
      ],
      technology: {
        title: 'Technologie',
        stack: [
          'Medische PPG-sensoren voor hartslag en SpO2',
          'Geavanceerde 3-assige accelerometer en gyroscoop',
          'Huidtemperatuursensor',
          'TensorFlow Lite AI-modellen voor on-device valdetectie',
          'Laag-vermogen Bluetooth en LTE-M connectiviteit',
          'IP68 waterbestendigheid voor doucheveiligheid',
          '7 dagen batterijduur met draadloos opladen'
        ]
      },
      roadmap: {
        title: 'Ontwikkelingstraject',
        phases: [
          { period: 'Q1 2026', title: 'Prototype Ontwikkeling', status: 'Gepland' },
          { period: 'Q2-Q3 2026', title: 'Klinisch Testen met Oudere Vrijwilligers', status: 'Gepland' },
          { period: 'Q4 2026', title: 'AI-Model Training & Validatie', status: 'Gepland' },
          { period: 'Q1-Q2 2027', title: 'MDR Certificeringsproces', status: 'Gepland' }
        ]
      },
      impact: {
        title: 'Verwachte Impact',
        metrics: [
          { metric: '85%', label: 'Valdetectie Nauwkeurigheid', icon: Award },
          { metric: '<30 sec', label: 'Noodresponstijd', icon: TrendingUp },
          { metric: '24/7', label: 'Continue Monitoring', icon: Activity },
          { metric: '100%', label: 'Familie Gemoedsrust', icon: Users }
        ]
      },
      cta: {
        title: 'Doe Mee',
        text: 'Geïnteresseerd in partneren, investeren of samenwerken aan dit project?',
        button: 'Neem Contact Op',
        back: 'Terug naar Projecten'
      }
    }
  };

  const t = content[language] || content.en;

  return (
    <div className="min-h-screen bg-[#0B1220]">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-[#06B6D4]/20 via-[#0B1220] to-[#7C3AED]/20 border-b border-[#1F2A3A] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-xl bg-[#06B6D4]/20 flex items-center justify-center">
              <Watch size={32} className="text-[#06B6D4]" />
            </div>
            <div>
              <Badge className="bg-[#06B6D4]/20 text-[#06B6D4] border-[#06B6D4]/30 mb-2">
                {t.badge}
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-white">
                {t.title}
              </h1>
            </div>
          </div>
          <p className="text-[#8A90A0] text-xl max-w-3xl">
            {t.subtitle}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Overview */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t.overview.title}</h2>
          <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-2xl p-8 border border-[#1F2A3A]">
            <p className="text-[#E6E8EF] text-lg leading-relaxed mb-4">
              {t.overview.text1}
            </p>
            <p className="text-[#8A90A0] leading-relaxed">
              {t.overview.text2}
            </p>
          </div>
        </section>

        {/* The Problem */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t.problem.title}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {t.problem.cards.map((card, index) => (
              <div key={index} className="bg-gradient-to-br from-red-500/10 to-red-500/5 backdrop-blur-sm rounded-xl p-6 border border-red-500/30">
                <h3 className="text-xl font-semibold text-white mb-3">{card.title}</h3>
                <p className="text-[#8A90A0] text-sm leading-relaxed">
                  {card.text}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Solution - Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-2">{t.solution.title}</h2>
          <p className="text-[#8A90A0] text-lg mb-8">{t.solution.subtitle}</p>
          <div className="grid md:grid-cols-2 gap-8">
            {t.features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-xl p-6 border border-[#06B6D4]/20 hover:border-[#06B6D4]/50 transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-[#06B6D4]/20 flex items-center justify-center">
                      <Icon size={24} className="text-[#06B6D4]" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">{feature.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {feature.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#8A90A0]">
                        <CheckCircle size={16} className="text-[#06B6D4] mt-1 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        {/* Technology */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t.technology.title}</h2>
          <div className="bg-gradient-to-br from-[#06B6D4]/10 to-[#7C3AED]/10 backdrop-blur-sm rounded-2xl p-8 border border-[#06B6D4]/30">
            <div className="grid md:grid-cols-2 gap-4">
              {t.technology.stack.map((tech, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#06B6D4] mt-0.5 flex-shrink-0" />
                  <p className="text-[#E6E8EF]">{tech}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Roadmap */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t.roadmap.title}</h2>
          <div className="space-y-4">
            {t.roadmap.phases.map((phase, index) => (
              <div key={index} className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A] flex items-center justify-between">
                <div>
                  <span className="text-[#06B6D4] text-sm font-semibold">{phase.period}</span>
                  <h3 className="text-xl font-semibold text-white mt-1">{phase.title}</h3>
                </div>
                <Badge variant="outline" className="text-[#8A90A0] border-[#8A90A0]/30">
                  {phase.status}
                </Badge>
              </div>
            ))}
          </div>
        </section>

        {/* Impact */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t.impact.title}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {t.impact.metrics.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-[#06B6D4]/20 to-transparent backdrop-blur-sm rounded-xl p-6 border border-[#06B6D4]/30 text-center">
                  <Icon size={32} className="text-[#06B6D4] mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white mb-2">{item.metric}</div>
                  <div className="text-[#8A90A0] text-sm">{item.label}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-[#06B6D4]/10 to-[#7C3AED]/10 backdrop-blur-sm rounded-2xl p-8 border border-[#06B6D4]/30">
          <h2 className="text-2xl font-bold text-white mb-4">{t.cta.title}</h2>
          <p className="text-[#8A90A0] mb-6">{t.cta.text}</p>
          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-[#06B6D4] hover:bg-[#06B6D4]/80 text-white"
            >
              {t.cta.button}
              <ArrowRight size={20} className="ml-2" />
            </Button>
            <Button 
              onClick={() => navigate('/projects')}
              variant="outline"
              className="border-[#8A90A0]/30 text-white hover:bg-[#1F2A3A]/50"
            >
              {t.cta.back}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ElderBand;
